import {
  Alert,
  Box,
  Button,
  Card,
  Group,
  Modal,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { SettingsTabs } from '../../components/app-shell/SettingsTabs';
import { MotionSection, PageHero } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';
import { csrfHeaders, useAuth } from '../../lib/auth-context';
import { showToast } from '../../lib/toast';

/**
 * /settings/privacy — self-serve data export request + close-account flow.
 *
 * Both endpoints are stub-aware: when the worker returns 501 with a known
 * `code`, we surface the trust-and-safety fallback copy. Once the schema +
 * state-machine extensions land (see worker/routes/users.ts header), the
 * same UI flips to the real flow with no code changes.
 */

export const Route = createFileRoute('/settings/privacy')({
  component: PrivacySettingsPage,
});

type ExportRequest = {
  id: string;
  scope: 'all' | 'profile_only' | 'messages_only';
  state: 'pending' | 'approved' | 'building' | 'ready' | 'expired' | 'cancelled';
  requested_at: string;
  ready_at: string | null;
  expires_at: string | null;
};

type ExportListResponse = {
  ok: true;
  items: ExportRequest[];
};

type ExportScope = 'all' | 'profile_only' | 'messages_only';

function PrivacySettingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const exports = useQuery({
    queryKey: ['users', 'me', 'data-export'],
    queryFn: async (): Promise<ExportListResponse> => {
      const res = await fetchOk('/api/users/me/data-export', { credentials: 'include' });
      return (await res.json()) as ExportListResponse;
    },
    // Empty list is a valid response under the stub. Don't retry on 501.
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 501) return false;
      return count < 2;
    },
  });

  const inFlight = (exports.data?.items ?? []).find(
    (it) => it.state === 'pending' || it.state === 'approved' || it.state === 'building',
  );

  const [scope, setScope] = useState<ExportScope>('all');
  const requestExport = useMutation({
    mutationFn: async () => {
      const res = await fetchOk('/api/users/me/data-export', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({ scope }),
      });
      return (await res.json()) as { ok: true };
    },
    onSuccess: () => {
      showToast({
        kind: 'success',
        body: "Recorded. We'll let you know when the file is ready.",
      });
      void qc.invalidateQueries({ queryKey: ['users', 'me', 'data-export'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          showToast({
            kind: 'error',
            body: 'You already have an export in progress.',
          });
          return;
        }
        showToast({ kind: 'error', body: err.message });
        void qc.invalidateQueries({ queryKey: ['users', 'me', 'data-export'] });
        return;
      }
      showToast({ kind: 'error', body: 'Could not record your request.' });
    },
  });

  const [closeOpen, { open: openClose, close: closeClose }] = useDisclosure(false);
  const [emailConfirm, setEmailConfirm] = useState('');
  const [reason, setReason] = useState('');
  const closeAccount = useMutation({
    mutationFn: async () => {
      const res = await fetchOk('/api/users/me/close-account', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({
          confirmEmail: emailConfirm,
          ...(reason.trim().length > 0 ? { reasonNote: reason.trim() } : {}),
        }),
      });
      return (await res.json()) as { ok: true; closingAt: string | null };
    },
    onSuccess: () => {
      closeClose();
      setEmailConfirm('');
      setReason('');
      // Wave 5O: /me now reports status='pending_close' and the ClosingBanner
      // surfaces at the top of the shell. Refresh /api/auth/me so the banner
      // shows immediately.
      showToast({
        kind: 'success',
        body: 'Closure recorded. You have 30 days to change your mind.',
      });
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        // 400 = email mismatch; 409 = wrong account state; 422 = zod validation.
        if (err.status === 400 || err.status === 409 || err.status === 422) {
          showToast({ kind: 'error', body: err.message });
          return;
        }
        showToast({ kind: 'error', body: err.message });
        return;
      }
      showToast({ kind: 'error', body: 'Could not record your closure.' });
    },
  });

  const emailMatches =
    user !== null && emailConfirm.trim().toLowerCase() === user.email.toLowerCase();

  return (
    <AppShell tone="form">
      <Stack gap="lg">
          <MotionSection>
            <PageHero
              eyebrow="Settings"
              title="Privacy & data"
              lede="Export a copy of what we hold, or close your account."
            />
          </MotionSection>

          <SettingsTabs current="privacy" />

          {/* ---------- Data export ---------- */}
          <Card withBorder padding="lg" radius="md">
            <Stack gap="sm">
              <Text fw={600}>Request a data export</Text>
              <Text size="sm" c="dimmed">
                Your profile, exchanges, messages, and reviews, packaged as a zip
                file. An admin reviews every request before the file is built;
                you'll get an email with a 14-day download link once it's ready.
              </Text>

              {inFlight ? (
                <Alert color="brand" variant="light">
                  An export is in progress, requested{' '}
                  {formatRelative(inFlight.requested_at)}. We'll email you when
                  it's ready.
                </Alert>
              ) : (
                <Stack gap="xs">
                  <Radio.Group
                    label="What to include"
                    value={scope}
                    onChange={(v) => setScope(v as ExportScope)}
                  >
                    <Stack gap={6} mt={6}>
                      <Radio value="all" label="Everything (recommended)" />
                      <Radio
                        value="profile_only"
                        label="Profile and account only"
                      />
                      <Radio
                        value="messages_only"
                        label="Messages and exchanges only"
                      />
                    </Stack>
                  </Radio.Group>
                  <div>
                    <Button
                      variant="light"
                      onClick={() => requestExport.mutate()}
                      loading={requestExport.isPending}
                    >
                      Request data export
                    </Button>
                  </div>
                </Stack>
              )}

              {(exports.data?.items.length ?? 0) > 0 ? (
                <Box pt="md" mt="xs" style={{ borderTop: '1px solid var(--mantine-color-ink-1)' }}>
                  <Text size="sm" fw={500} mb={6}>
                    Recent exports
                  </Text>
                  <Stack gap={4}>
                    {(exports.data?.items ?? []).map((it) => (
                      <ExportRow key={it.id} item={it} />
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          </Card>

          {/* ---------- Close account ---------- */}
          <Card withBorder padding="lg" radius="md">
            <Stack gap="sm">
              <Text fw={600}>Close your account</Text>
              <Text size="sm" c="dimmed">
                You sign out immediately. For 30 days you can sign back in to
                restore the account; after that, it hard-deletes. Past exchanges
                and reviews stay on the record, attributed to "Former member."
              </Text>
              <Text size="sm" c="dimmed">
                Live exchanges aren't auto-cancelled. Your counterparties can
                still complete or cancel them. Pending requests you sent
                automatically cancel.
              </Text>
              <div>
                <Button color="red" variant="outline" onClick={openClose}>
                  Close account
                </Button>
              </div>
            </Stack>
          </Card>

          <Modal
            opened={closeOpen}
            onClose={closeClose}
            title="Close your account"
            centered
            size="md"
          >
            <Stack gap="md">
              <Alert color="red" variant="light">
                After 30 days, this is permanent. Past exchanges remain on
                record under "Former member." Counterparties of live exchanges
                will be notified.
              </Alert>
              <TextInput
                label="Type your email to confirm"
                description={
                  user !== null
                    ? `We need to see ${user.email} typed in full.`
                    : 'Sign in first.'
                }
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.currentTarget.value)}
                autoComplete="off"
                spellCheck={false}
              />
              <Textarea
                label="What's prompting this? (optional)"
                description="Logged with your closure record. Helps us know what to fix."
                autosize
                minRows={3}
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
              />
              <Group justify="flex-end">
                <Button variant="default" onClick={closeClose}>
                  Keep my account
                </Button>
                <Button
                  color="red"
                  onClick={() => closeAccount.mutate()}
                  loading={closeAccount.isPending}
                  disabled={!emailMatches}
                >
                  Close account
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Stack>
    </AppShell>
  );
}

function ExportRow({ item }: { item: ExportRequest }) {
  const stateColor: Record<ExportRequest['state'], string> = {
    pending: 'brand',
    approved: 'brand',
    building: 'brand',
    ready: 'teal',
    expired: 'gray',
    cancelled: 'gray',
  };

  return (
    <Group justify="space-between" gap="xs" wrap="nowrap">
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="sm" fw={500}>
          {scopeLabel(item.scope)}: {item.state.replace('_', ' ')}
        </Text>
        <Text size="xs" c="dimmed">
          Requested {formatRelative(item.requested_at)}
          {item.expires_at ? ` · available until ${formatDate(item.expires_at)}` : ''}
        </Text>
      </Stack>
      {item.state === 'ready' ? (
        <Button
          component="a"
          size="xs"
          variant="light"
          href={`/api/users/me/data-export/${item.id}/download`}
          // The download endpoint returns a redirect to a signed R2 URL. No
          // CSRF needed because it's a GET; the cookie carries the auth.
        >
          Download
        </Button>
      ) : null}
    </Group>
  );
}

function scopeLabel(scope: ExportRequest['scope']): string {
  if (scope === 'all') return 'Everything';
  if (scope === 'profile_only') return 'Profile only';
  return 'Messages only';
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
